import React, { useState } from 'react';
import { TwilioService, TwilioResponse } from './services/twilioService';

function App() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [response, setResponse] = useState<TwilioResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const validatePhoneNumber = (phone: string): boolean => {
    return /^\+[1-9]\d{7,14}$/.test(phone);
  };

  const showResult = (result: TwilioResponse) => {
    setResponse(result);
  };

  const handleCheckNumber = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      showResult({ success: false, error: 'Please enter a valid phone number in E.164 format (e.g. +4231234567)' });
      return;
    }

    setLoading(true);
    const result = await TwilioService.checkNumber(phoneNumber);
    setLoading(false);
    showResult(result);
  };

  const handleAddNumber = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      showResult({ success: false, error: 'Please enter a valid phone number in E.164 format (e.g. +4231234567)' });
      return;
    }

    setLoading(true);
    const result = await TwilioService.addNumber(phoneNumber);
    setLoading(false);
    showResult(result);
  };

  const handleRemoveNumber = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      showResult({ success: false, error: 'Please enter a valid phone number in E.164 format (e.g. +4231234567)' });
      return;
    }

    setLoading(true);
    const result = await TwilioService.removeNumber(phoneNumber);
    setLoading(false);
    showResult(result);
  };

  const isValid = validatePhoneNumber(phoneNumber);

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* HEADER */}
      <header className="bg-primary text-white p-4 flex justify-between items-center shadow">
        <img src="https://tantumpay.com/wp-content/uploads/2020/10/tantumpay-logo-icon.png" alt="Tantum Logo" className="h-10" />
      </header>

      {/* HERO */}
      <section className="text-center p-10 bg-white shadow">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary">Twilio Phone Number Management</h2>
        <p className="text-muted max-w-2xl mx-auto">
          Manage your Twilio SafeList without sensitive data in the code. Use the three functions: check number, add, or remove.
        </p>
      </section>

      {/* TOOL */}
      <main className="centered-container">
        <div className="main-card max-w-2xl w-full mx-auto">
          <div className="space-y-6">
            <div className="input-group">
              <label className="input-label">Phone Number in E.164 Format <span className="text-red-500">*</span></label>
              <input 
                type="tel" 
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="input-field" 
                placeholder="e.g. +4231234567"
              />
              <p className="input-hint">Format: +Country Code + Number (e.g. +4231234567)</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={handleCheckNumber} 
                disabled={!isValid || loading}
                className="button button-primary flex-1"
              >
                {loading ? 'Checking...' : 'Check Number'}
              </button>
              <button 
                onClick={handleAddNumber} 
                disabled={!isValid || loading}
                className="button button-success flex-1"
              >
                {loading ? 'Adding...' : 'Add Number'}
              </button>
              <button 
                onClick={handleRemoveNumber} 
                disabled={!isValid || loading}
                className="button button-danger flex-1"
              >
                {loading ? 'Removing...' : 'Remove Number'}
              </button>
            </div>
          </div>

          {response && (
            <div>
              <div className="status-message">
                <span className="status-icon">{response.success ? '✅' : '❌'}</span>
                {response.success ? 'Number is in the Safe List:' : 'Error:'}
              </div>
              {response.success && response.data && (
                <div className="json-output">
                  {JSON.stringify(response.data, null, 2)}
                </div>
              )}
              {response.error && (
                <div className="json-output">
                  {response.error}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* FOOTER */}
      <footer className="footer flex flex-col md:flex-row justify-between text-sm">
        <div className="space-x-4 mb-2 md:mb-0">
          <a href="https://tantumpay.com/en/imprint/" className="hover:underline">Imprint</a>
          <a href="https://tantumpay.com/en/privacy-policy/" className="hover:underline">Privacy Policy</a>
          <a href="https://tantumpay.com/terms-and-conditions/" className="hover:underline">Terms & Conditions</a>
        </div>
        <div>&copy; 2025 Tantum AG. All rights reserved.</div>
      </footer>
    </div>
  );
}

export default App;
